/**
 * Mapbox GL wrapper: style/theme, vehicle puck, route rendering with live
 * congestion colouring, alternative routes, destination marker, and the
 * navigation camera loop.
 */

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN, REGION } from '../core/config';
import { loadStrippedStyle } from './styleLoader';
import type { Fix, LngLat, NavProgress, Route } from '../core/types';
import { bounds, lerpBearing } from '../core/geo';
import { computeCamera, previewPadding } from './camera';

const SRC_ROUTE = 'wf-route';
const SRC_ALTS = 'wf-route-alts';
const SRC_DEST = 'wf-destination';
const SRC_PLACES = 'wf-places';
const SRC_DEM = 'wf-dem';
const SRC_DRIVEN = 'wf-driven';

export interface SavedPlaceMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  kind: string;
  creatorName: string;
}

export interface MapViewEvents {
  onLongPress?: (point: LngLat) => void;
  onAltRouteClick?: (routeId: string) => void;
  /** User panned/rotated during navigation - switch to browse mode. */
  onUserGesture?: () => void;
  onPlaceClick?: (place: SavedPlaceMarker) => void;
}

type CongestionSegment = {
  coords: LngLat[];
  level: 'unknown' | 'low' | 'moderate' | 'heavy' | 'severe';
};

const ROUTE_BLUE = '#2f7dff';

/** Finger wobble tolerated during a long-press, in screen pixels. */
const LONG_PRESS_SLOP = 14;

const CONGESTION_COLORS: Record<CongestionSegment['level'], string> = {
  unknown: ROUTE_BLUE,
  low: ROUTE_BLUE,
  moderate: '#ffb020',
  heavy: '#ff6a45',
  severe: '#d7263d',
};

/**
 * Route line width as a function of zoom, scaled by `factor`.
 *
 * The old fixed 7 px line disappeared under the vehicle at driving zoom - the
 * driver couldn't tell which of two parallel carriageways was theirs. Widths
 * now grow with zoom the way Mapbox's own road layers do, so the route stays
 * proportionate to the streets under it at every scale.
 */
function widthAt(factor: number): mapboxgl.DataDrivenPropertyValueSpecification<number> {
  return [
    'interpolate',
    ['exponential', 1.4],
    ['zoom'],
    8,
    4 * factor,
    12,
    8 * factor,
    15,
    15 * factor,
    17,
    23 * factor,
    20,
    34 * factor,
  ];
}

export class MapView {
  readonly map: mapboxgl.Map;
  private puckEl: HTMLDivElement;
  private puck: mapboxgl.Marker;
  private destMarker: mapboxgl.Marker | null = null;
  private ready = false;
  private queued: Array<() => void> = [];
  private displayedHeading = 0;
  private following = true;
  private navigating = false;
  private longPressTimer: number | null = null;
  private pressOrigin: mapboxgl.Point | null = null;
  private theme: 'night' | 'day' = 'day';

  constructor(
    container: HTMLElement,
    style: object | string,
    private events: MapViewEvents = {},
  ) {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container,
      style: style as string,
      center: REGION.fallbackCenter,
      zoom: 12,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      // We drive the camera ourselves; disable anything that fights it.
      pitchWithRotate: false,
      touchPitch: false,
      fadeDuration: 150,
    });
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    this.map.touchZoomRotate.disableRotation();

    // The vehicle marker: a heading arrow, drawn large. The original was a
    // ~36 px arrow inside a 52 px box and disappeared among the street
    // furniture at driving zoom; this keeps the arrow shape the driver asked
    // for but at roughly double the size, with a heavy outline so it reads
    // against both the light and dark map themes.
    this.puckEl = document.createElement('div');
    this.puckEl.className = 'puck';
    this.puckEl.innerHTML = `
      <div class="puck__halo"></div>
      <svg class="puck__arrow" viewBox="0 0 64 64" aria-hidden="true">
        <path d="M32 5 L52 53 L32 42 L12 53 Z" />
      </svg>`;
    this.puck = new mapboxgl.Marker({
      element: this.puckEl,
      rotationAlignment: 'map',
      pitchAlignment: 'map',
    })
      .setLngLat(REGION.fallbackCenter)
      .addTo(this.map);
    this.puckEl.style.visibility = 'hidden';

    this.map.on('load', () => {
      this.addLayers();
      this.ready = true;
      for (const fn of this.queued) fn();
      this.queued = [];
    });

    // If the container was mid-layout at construction, the canvas keeps
    // mapbox's tiny fallback size - re-measure on the next frames.
    requestAnimationFrame(() => this.map.resize());
    setTimeout(() => this.map.resize(), 400);

    // Any deliberate gesture breaks camera-follow until re-centred.
    const gesture = (e: { originalEvent?: Event } | unknown) => {
      if (!(e as { originalEvent?: Event }).originalEvent) return; // programmatic move
      if (this.following) {
        this.following = false;
        this.events.onUserGesture?.();
      }
    };
    this.map.on('dragstart', gesture);
    this.map.on('zoomstart', gesture);
    this.map.on('rotatestart', gesture);

    // Long-press to drop a pin. Any movement used to cancel it outright, which
    // made pinning a soi nearly impossible from a car mount - holding a finger
    // perfectly still on a vibrating dashboard is not realistic. A small
    // wobble is now tolerated; only a real pan cancels.
    this.map.on('touchstart', (e) => {
      if (e.originalEvent.touches.length !== 1) return;
      const at = e.lngLat;
      this.pressOrigin = e.point;
      this.longPressTimer = window.setTimeout(() => {
        this.longPressTimer = null;
        this.events.onLongPress?.([at.lng, at.lat]);
      }, 550);
    });
    const cancelPress = () => {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.pressOrigin = null;
    };
    this.map.on('touchmove', (e) => {
      if (this.longPressTimer === null || !this.pressOrigin) return;
      const dx = e.point.x - this.pressOrigin.x;
      const dy = e.point.y - this.pressOrigin.y;
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP) cancelPress();
    });
    this.map.on('touchend', cancelPress);
    this.map.on('touchcancel', cancelPress);
    this.map.on('contextmenu', (e) => this.events.onLongPress?.([e.lngLat.lng, e.lngLat.lat]));
  }

  private whenReady(fn: () => void): void {
    if (this.ready) fn();
    else this.queued.push(fn);
  }

  /**
   * Id of the style's first symbol layer.
   *
   * Route lines are inserted BEFORE it so that road-number shields, street
   * names and place labels draw on top of the route instead of underneath it.
   * Without this the thick blue line sits over the very road numbers the
   * driver is trying to read - reported from the road as "the road numbers are
   * under the route, we don't see them at all".
   */
  private firstSymbolLayerId(): string | undefined {
    try {
      return this.map.getStyle()?.layers?.find((l) => l.type === 'symbol')?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Where the route line has to be inserted to sit above the roads.
   *
   * "Above the first symbol layer" is the usual advice and it is wrong here.
   * In `navigation-day-v1` the first symbol layer is `turning-feature-outline-
   * navigation` at index 29, while the actual road lines run from index 30 to
   * 95 - so anchoring to it buried the route beneath every road, tunnel and
   * bridge in the style. Reported from the road twice: "the route is not
   * visible, its under everything."
   *
   * Anchor instead to the layer immediately after the LAST road-ish line. That
   * puts the route above all carriageways and below every label and shield,
   * which is the ordering we actually want.
   */
  private routeAnchorId(): string | undefined {
    try {
      const layers = this.map.getStyle()?.layers ?? [];
      let last = -1;
      for (let i = 0; i < layers.length; i++) {
        const l = layers[i] as { type: string; 'source-layer'?: string };
        if (l.type !== 'line' && l.type !== 'fill') continue;
        if (l['source-layer'] === 'road') last = i;
      }
      if (last >= 0 && layers[last + 1]) return layers[last + 1].id;
    } catch {
      /* fall through */
    }
    return this.firstSymbolLayerId();
  }



  /**
   * Terrain relief and extruded buildings.
   *
   * Both are free on the tier this app already uses: the buildings come from
   * the `composite` vector source the style has loaded anyway (no extra
   * requests at all), and `mapbox.terrain-rgb` DEM tiles are billed as ordinary
   * tile requests against the same free allowance rather than as a paid add-on.
   *
   * Terrain is kept subtle deliberately. A dramatic exaggeration looks good in
   * a screenshot and is actively unhelpful at eye level in a car, where it tips
   * the road out of the frame on every rise. Buildings are only extruded from
   * z15 so a phone GPU is not asked to draw a city while you are on a motorway.
   */
  private addDepth(): void {
    try {
      if (!this.map.getSource(SRC_DEM)) {
        this.map.addSource(SRC_DEM, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        });
      }
      this.map.setTerrain({ source: SRC_DEM, exaggeration: 1.1 });
    } catch {
      /* DEM unavailable (offline, or the token lacks it) - flat map is fine */
    }

    try {
      if (this.map.getLayer('wf-buildings')) return;
      // The style already ships this source; we only add the extrusion.
      if (!this.map.getSource('composite')) return;
      // Buildings must go BENEATH the route. Added on top - which is what
      // happens if you append without a beforeId - a 55%-opaque grey slab
      // covers the route line, and the driver reported exactly that: "the
      // route is not visible, it's under everything".
      const under = this.map.getLayer('wf-route-glow')
        ? 'wf-route-glow'
        : this.routeAnchorId();
      this.map.addLayer(
        {
          id: 'wf-buildings',
          source: 'composite',
          'source-layer': 'building',
          type: 'fill-extrusion',
          minzoom: 15,
          filter: ['==', ['get', 'extrude'], 'true'],
          paint: {
            // Warm stone by day, cool slate at night - grey buildings on a
            // grey map was the whole complaint.
            'fill-extrusion-color': this.theme === 'night' ? '#39414f' : '#d9c9b4',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 16.5, ['get', 'height']],
            'fill-extrusion-base': ['get', 'min_height'],
            // Kept faint as well as underneath: at driving zoom they are
            // context, not content.
            'fill-extrusion-opacity': 0.4,
          },
        },
        under,
      );
    } catch {
      /* style without a building layer - nothing to extrude */
    }
  }

  private addLayers(): void {
    const beforeId = this.routeAnchorId();
    // Alternatives go underneath the active route.
    this.map.addSource(SRC_ALTS, { type: 'geojson', data: emptyFC() });
    this.map.addLayer({
      id: 'wf-alts-casing',
      type: 'line',
      source: SRC_ALTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#080c14', 'line-width': widthAt(0.62), 'line-opacity': 0.75 },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-alts-line',
      type: 'line',
      source: SRC_ALTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#6b7a94', 'line-width': widthAt(0.42), 'line-opacity': 0.9 },
    }, beforeId);

    /*
     * Roads we have actually driven.
     *
     * "If we can drive it, there must be a road" - and often there isn't one
     * in OpenStreetMap. The access track to a hillside site, a yard, a new
     * bypass: none of them exist on the base map, but every one of them is in
     * our own trip logs. Drawing those tracks puts them back on the map, and
     * colouring by the speed actually achieved doubles as a picture of where
     * the fleet crawls.
     *
     * Sits beneath the route so it is context, never confused with guidance.
     */
    this.map.addSource(SRC_DRIVEN, { type: 'geojson', data: this.lastDrivenData ?? emptyFC() });
    this.map.addLayer(
      {
        id: 'wf-driven',
        type: 'line',
        source: SRC_DRIVEN,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#b39af7'],
          'line-width': widthAt(0.34),
          'line-opacity': 0.75,
        },
      },
      beforeId,
    );

    this.map.addSource(SRC_ROUTE, { type: 'geojson', data: emptyFC() });

    // Soft outer glow: lifts the route off a dark map without fattening the
    // line itself. This is what makes the road readable at a glance while
    // driving, which fixed-width 7 px never did.
    this.map.addLayer({
      id: 'wf-route-glow',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], ROUTE_BLUE],
        'line-width': widthAt(1.55),
        'line-opacity': 0.22,
        'line-blur': 10,
      },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-route-casing',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#04070e', 'line-width': widthAt(1.34), 'line-opacity': 0.95 },
    }, beforeId);
    this.map.addLayer({
      id: 'wf-route-line',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], ROUTE_BLUE],
        'line-width': widthAt(1),
      },
    }, beforeId);
    // A brighter core down the middle gives the line depth instead of reading
    // as a flat slab of blue.
    this.map.addLayer({
      id: 'wf-route-core',
      type: 'line',
      source: SRC_ROUTE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': widthAt(0.2),
        'line-opacity': 0.28,
      },
    }, beforeId);
    // Direction chevrons, so which way the route runs is never ambiguous.
    this.map.addLayer({
      id: 'wf-route-arrows',
      type: 'symbol',
      source: SRC_ROUTE,
      minzoom: 13,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 90,
        'text-field': '▶',
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 18, 16],
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': 0.75,
        'text-halo-color': 'rgba(0,0,0,0.35)',
        'text-halo-width': 1,
      },
    }, beforeId);

    this.map.addSource(SRC_DEST, { type: 'geojson', data: emptyFC() });

    // Fleet saved places: dot + label, amber for shared live locations.
    this.map.addSource(SRC_PLACES, { type: 'geojson', data: this.lastPlacesData ?? emptyFC() });
    this.map.addLayer({
      id: 'wf-places-dot',
      type: 'circle',
      source: SRC_PLACES,
      paint: {
        'circle-radius': 7,
        'circle-color': ['match', ['get', 'kind'], 'shared-location', '#f5a623', '#2fbf71'],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
    this.map.addLayer({
      id: 'wf-places-label',
      type: 'symbol',
      source: SRC_PLACES,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 12.5,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-optional': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.75)',
        'text-halo-width': 1.4,
      },
    });
    const placeClick = (e: { features?: unknown[] }) => {
      const f = e.features?.[0] as { properties?: SavedPlaceMarker } | undefined;
      if (f?.properties) this.events.onPlaceClick?.(f.properties);
    };
    this.map.on('click', 'wf-places-dot', placeClick);
    this.map.on('click', 'wf-places-label', placeClick);

    // Bigger, readable route numbers on the map itself.
    // Road numbers now live on the green sign at the top of the screen, where
    // they are read; scaling up the ones painted on the map only crowded it.
    this.addDepth();

    this.map.on('click', 'wf-alts-line', (e) => {
      const id = e.features?.[0]?.properties?.routeId as string | undefined;
      if (id) this.events.onAltRouteClick?.(id);
    });
    this.map.on('mouseenter', 'wf-alts-line', () => (this.map.getCanvas().style.cursor = 'pointer'));
    this.map.on('mouseleave', 'wf-alts-line', () => (this.map.getCanvas().style.cursor = ''));
  }

  async setTheme(theme: 'night' | 'day'): Promise<void> {
    // setStyle wipes custom sources/layers; re-add and re-draw afterwards.
    const routeData = this.lastRouteData;
    const altData = this.lastAltData;
    this.ready = false;
    document.documentElement.dataset.mapTheme = theme;
    this.theme = theme;
    // `diff: false` forces a full reload. The default diffing path tries to
    // reconcile the old style with the new one and, between two styles this
    // structurally different, quietly kept parts of the previous palette -
    // which is part of why switching to night never looked properly dark.
    // The typings mark every SetStyleOptions field required; only `diff` is
    // meaningful here, so narrow through Partial rather than widening to any.
    type SetStyleOptions = Parameters<mapboxgl.Map['setStyle']>[1];
    this.map.setStyle(
      (await loadStrippedStyle(theme)) as string,
      { diff: false } as Partial<SetStyleOptions> as SetStyleOptions,
    );
    this.map.once('style.load', () => {
      this.addLayers();
      this.ready = true;
      if (routeData) this.setSourceData(SRC_ROUTE, routeData);
      if (altData) this.setSourceData(SRC_ALTS, altData);
      if (this.lastDrivenData) this.setSourceData(SRC_DRIVEN, this.lastDrivenData);
      for (const fn of this.queued) fn();
      this.queued = [];
    });
  }

  private lastRouteData: GeoJSON.FeatureCollection | null = null;
  private lastAltData: GeoJSON.FeatureCollection | null = null;
  private lastPlacesData: GeoJSON.FeatureCollection | null = null;
  private lastDrivenData: GeoJSON.FeatureCollection | null = null;

  /**
   * Draw previously driven tracks. Each track is split into runs of similar
   * speed so the line shows where the fleet actually moves and where it
   * crawls, rather than being one flat colour.
   */
  setDrivenTracks(
    tracks: Array<Array<[number, number, number]>>,
    mode: 'speed' | 'rough' = 'speed',
  ): void {
    const bandOf = mode === 'rough' ? roughBand : speedBand;
    const palette = mode === 'rough' ? ROUGH_COLORS : SPEED_COLORS;
    const features: GeoJSON.Feature[] = [];
    for (const track of tracks) {
      if (track.length < 2) continue;
      let run: Array<[number, number]> = [[track[0][0], track[0][1]]];
      let band = bandOf(track[0][2]);
      for (let i = 1; i < track.length; i++) {
        const b = bandOf(track[i][2]);
        run.push([track[i][0], track[i][1]]);
        if (b !== band || i === track.length - 1) {
          if (run.length > 1) {
            features.push({
              type: 'Feature',
              properties: { color: palette[band] },
              geometry: { type: 'LineString', coordinates: run },
            });
          }
          run = [[track[i][0], track[i][1]]];
          band = b;
        }
      }
    }
    this.lastDrivenData = { type: 'FeatureCollection', features };
    this.whenReady(() => this.setSourceData(SRC_DRIVEN, this.lastDrivenData!));
  }

  clearDrivenTracks(): void {
    this.lastDrivenData = emptyFC();
    this.whenReady(() => this.setSourceData(SRC_DRIVEN, this.lastDrivenData!));
  }

  /** Render the fleet's saved places. Survives theme switches. */
  setPlaces(places: SavedPlaceMarker[]): void {
    this.lastPlacesData = {
      type: 'FeatureCollection',
      features: places.map((p) => ({
        type: 'Feature',
        properties: { ...p },
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      })),
    };
    this.whenReady(() => this.setSourceData(SRC_PLACES, this.lastPlacesData!));
  }

  private setSourceData(id: string, data: GeoJSON.FeatureCollection): void {
    const src = this.map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
    src?.setData(data);
  }

  /* ---------------------------- position ---------------------------- */

  updatePuck(fix: Fix, snappedTo?: LngLat, headingOverride?: number): void {
    const pos = snappedTo ?? [fix.lng, fix.lat];
    this.puckEl.style.visibility = 'visible';
    this.puck.setLngLat(pos as [number, number]);

    const target = headingOverride ?? fix.heading;
    if (target != null) {
      // Ease the arrow so heading noise doesn't make it twitch.
      this.displayedHeading = lerpBearing(this.displayedHeading, target, 0.35);
      this.puck.setRotation(this.displayedHeading);
    }
    this.puckEl.classList.toggle('puck--stale', Date.now() - fix.timestamp > 8000);
  }

  /* ---------------------------- camera ---------------------------- */

  startNavigation(): void {
    this.navigating = true;
    this.following = true;
  }

  stopNavigation(): void {
    this.navigating = false;
    this.map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
  }

  recenter(): void {
    this.following = true;
  }

  get isFollowing(): boolean {
    return this.following;
  }

  /** Called on every fix while navigating. */
  driveCamera(fix: Fix, progress: NavProgress | null): void {
    if (!this.navigating || !this.following) return;
    const heading =
      progress && fix.speed != null && fix.speed < 2
        ? progress.snapped.courseOnRoute // parked/slow: hold the road's bearing
        : (fix.heading ?? progress?.snapped.courseOnRoute ?? this.displayedHeading);

    const cam = computeCamera(
      progress,
      heading,
      fix.speed ?? 0,
      this.map.getContainer().clientHeight,
      this.map.getContainer().clientWidth,
    );
    this.map.easeTo({
      center: (progress?.snapped.location ?? [fix.lng, fix.lat]) as [number, number],
      zoom: cam.zoom,
      pitch: cam.pitch,
      bearing: cam.bearing,
      padding: cam.padding,
      duration: cam.duration,
      easing: (t) => t, // linear: successive eases chain into continuous motion
      essential: true,
    });
    this.updatePuck(fix, progress?.snapped.location, heading);
  }

  jumpTo(center: LngLat, zoom = 15): void {
    this.whenReady(() => this.map.easeTo({ center: center as [number, number], zoom, duration: 800 }));
  }

  fitRoute(routes: Route[], isPortrait: boolean): void {
    this.whenReady(() => {
      const pts = routes.flatMap((r) => r.geometry.coordinates);
      if (!pts.length) return;
      const [w, s, e, n] = bounds(pts);
      this.map.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding: previewPadding(isPortrait), pitch: 0, bearing: 0, duration: 700, maxZoom: 16 },
      );
    });
  }

  /* ---------------------------- routes ---------------------------- */

  /**
   * Draw the active route. With congestion annotation present, the line is
   * split into segments coloured by traffic level - the free-tier equivalent
   * of Google's red/orange overlays.
   */
  drawRoute(route: Route | null): void {
    this.whenReady(() => {
      if (!route) {
        this.lastRouteData = emptyFC();
        this.setSourceData(SRC_ROUTE, this.lastRouteData);
        return;
      }
      const features: GeoJSON.Feature[] = [];
      const congestion = route.legs[0]?.annotation?.congestion;
      const coords = route.geometry.coordinates;

      if (congestion && congestion.length >= coords.length - 1) {
        for (const seg of mergeCongestion(coords, congestion)) {
          features.push({
            type: 'Feature',
            properties: { color: CONGESTION_COLORS[seg.level] },
            geometry: { type: 'LineString', coordinates: seg.coords },
          });
        }
      } else {
        features.push({
          type: 'Feature',
          properties: { color: CONGESTION_COLORS.low },
          geometry: route.geometry,
        });
      }
      this.lastRouteData = { type: 'FeatureCollection', features };
      this.setSourceData(SRC_ROUTE, this.lastRouteData);
    });
  }

  drawAlternatives(routes: Route[]): void {
    this.whenReady(() => {
      this.lastAltData = {
        type: 'FeatureCollection',
        features: routes.map((r) => ({
          type: 'Feature',
          properties: { routeId: r._id },
          geometry: r.geometry,
        })),
      };
      this.setSourceData(SRC_ALTS, this.lastAltData);
    });
  }

  setDestination(point: LngLat | null): void {
    this.whenReady(() => {
      this.destMarker?.remove();
      this.destMarker = null;
      if (!point) return;
      const el = document.createElement('div');
      el.className = 'dest-marker';
      el.innerHTML = `<svg viewBox="0 0 24 24"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>`;
      this.destMarker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(point as [number, number])
        .addTo(this.map);
    });
  }

  /**
   * Has the map been moved away from the vehicle?
   *
   * Judged in screen space rather than by distance, because "far" depends
   * entirely on zoom: 200 m off-centre is nothing at z12 and completely off
   * the display at z18. The margin keeps the button from appearing when the
   * arrow is merely near an edge but still visible.
   */
  vehicleOffScreen(at: LngLat | null): boolean {
    if (!at || !this.ready) return false;
    try {
      const p = this.map.project(at as [number, number]);
      const c = this.map.getContainer();
      const margin = 70;
      return (
        p.x < margin ||
        p.y < margin ||
        p.x > c.clientWidth - margin ||
        p.y > c.clientHeight - margin
      );
    } catch {
      return false;
    }
  }

  resize(): void {
    this.map.resize();
  }
}

/** Speed bands for the driven-roads overlay, km/h. */
const SPEED_COLORS = ['#d7263d', '#ff8c42', '#ffd23f', '#6fe3a8', '#3ddc97'];
/** Roughness bands: green is smooth, red is punishing. */
const ROUGH_COLORS = ['#3ddc97', '#a7e05a', '#ffd23f', '#ff8c42', '#d7263d'];

function roughBand(index: number): number {
  if (index < 0) return 0; // unmeasured
  if (index < 18) return 0;
  if (index < 30) return 1;
  if (index < 42) return 2;
  if (index < 58) return 3;
  return 4;
}

function speedBand(kmh: number): number {
  if (kmh < 15) return 0;
  if (kmh < 35) return 1;
  if (kmh < 60) return 2;
  if (kmh < 90) return 3;
  return 4;
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/** Merge per-vertex congestion into contiguous same-level line segments. */
function mergeCongestion(
  coords: LngLat[],
  congestion: Array<CongestionSegment['level']>,
): CongestionSegment[] {
  const out: CongestionSegment[] = [];
  let current: CongestionSegment | null = null;
  for (let i = 0; i < coords.length - 1; i++) {
    const level = congestion[i] ?? 'unknown';
    if (!current || current.level !== level) {
      current = { level, coords: [coords[i], coords[i + 1]] };
      out.push(current);
    } else {
      current.coords.push(coords[i + 1]);
    }
  }
  return out;
}
